import axios, { type AxiosError, type AxiosResponse } from 'axios';

export const API_ERROR_CODES = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  VALIDATION: 422,
} as const;

export type ApiErrorCode =
  (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

export class ApiError extends Error {
  readonly status?: number;
  readonly data?: unknown;

  constructor(
    message: string,
    options?: {
      status?: number;
      data?: unknown;
      cause?: unknown;
    },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = options?.status;
    this.data = options?.data;

    if (options?.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

export function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

/**
 * Backend-ийн Zod validation алдаа (`{ message: 'Validation failed', errors: [{ message, path }] }`)-аас
 * талбар бүрийн бодит мессежийг цуглуулна. Давхардсан мессежийг нэг болгоно.
 */
function getValidationErrorMessages(data: object): string[] {
  if (!('errors' in data) || !Array.isArray(data.errors)) {
    return [];
  }

  const messages = data.errors
    .map((item: unknown) =>
      typeof item === 'string'
        ? item
        : typeof item === 'object' &&
            item !== null &&
            'message' in item &&
            typeof item.message === 'string'
          ? item.message
          : '',
    )
    .map((message) => message.trim())
    .filter(Boolean);

  return [...new Set(messages)];
}

export function getErrorMessageFromResponseData(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) {
    return undefined;
  }

  // "Validation failed" гэх мэт ерөнхий мессежийн оронд талбарын бодит алдааг харуулна
  const validationMessages = getValidationErrorMessages(data);
  if (validationMessages.length > 0) {
    return validationMessages.join('\n');
  }

  if ('message' in data && typeof data.message === 'string') {
    return data.message;
  }

  return undefined;
}

export function parseApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data;
    const message =
      getErrorMessageFromResponseData(data) ??
      error.message ??
      'API request failed';

    return new ApiError(message, {
      status,
      data,
      cause: error,
    });
  }

  if (error instanceof Error) {
    return new ApiError(error.message, { cause: error });
  }

  return new ApiError('Unknown API error', { cause: error });
}

export function assertValidObjectResponse(response: AxiosResponse): void {
  if (response.status !== 200) {
    return;
  }

  if (!response.data || typeof response.data !== 'object') {
    throw new ApiError('Invalid data format');
  }
}

export function createObjectResponseInterceptor() {
  return {
    onFulfilled: (response: AxiosResponse) => {
      assertValidObjectResponse(response);
      return response;
    },
  };
}

export function isUnauthorizedError(error: unknown): boolean {
  return parseApiError(error).status === API_ERROR_CODES.UNAUTHORIZED;
}

export function getMutationErrorMessage(
  error: unknown,
  fallback = 'Алдаа гарлаа',
): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  return getErrorMessageFromResponseData(data) || fallback;
}
